/**
 * Pure, PRESENTATIONAL ship-stat derivations — the client's legibility layer.
 *
 * The authoritative combat maths live in the server's `shipStats()`
 * (apps/server/src/persistence/types.ts); the CLIENT can't see that. So these
 * pure helpers re-derive the SAME numbers from the shared SHIP_CLASS / SHIP /
 * SHIP_UPGRADE / RAID constants, letting the shipyard + Harbor Works + speedo
 * SHOW a captain exactly what a hull choice or an upgrade level buys.
 *
 * No wire, no sim state, no side effects — just constants → readouts. Every
 * formula here mirrors the server's `shipStats()`; the shared SHIP_UPGRADE
 * constants keep the per-level steps from drifting.
 */
import {
  SHIP, SHIP_CLASS, SHIP_CLASS_COUNT, SHIP_UPGRADE, RAID,
  ShipClassId, LEGACY_SHIP_CLASS,
} from './config.js';

/** The 4 universal upgrade levels a hull carries (client mirror of the server ShipLoadout, class aside). */
export interface UpgradeLevels {
  hullLvl: number;
  speedLvl: number;
  damageLvl: number;
  cargoLvl: number;
}

const resolveCls = (cls: number): ShipClassId =>
  (SHIP_CLASS[cls as ShipClassId] ? cls : LEGACY_SHIP_CLASS) as ShipClassId;

/** Speed multiplier (class × Speed upgrade) — matches server shipStats().speedMul. */
export function speedMulOf(cls: number, speedLvl: number): number {
  return SHIP_CLASS[resolveCls(cls)].speedMul * (1 + (speedLvl - 1) * SHIP_UPGRADE.SPEED_PER_LVL);
}

/** Rated top speed (m/s) = MAX_SPEED × speedMul — the CEILING the ship hits with full sail, running with
 *  the wind. Upgrades + class move THIS number; the live speed varies below it purely with the wind. */
export function topSpeedMs(cls: number, speedLvl: number): number {
  return SHIP.MAX_SPEED * speedMulOf(cls, speedLvl);
}

/** Turn multiplier (class × Speed upgrade) — matches server shipStats().turnMul. */
export function turnMulOf(cls: number, speedLvl: number): number {
  return SHIP_CLASS[resolveCls(cls)].turnMul * (1 + (speedLvl - 1) * SHIP_UPGRADE.TURN_PER_LVL);
}

/** Cannon-damage BONUS as a fraction (0 at Lv1, +12%/level) — matches server damageMul−1. */
export function damageBonusOf(damageLvl: number): number {
  return (damageLvl - 1) * SHIP_UPGRADE.DAMAGE_PER_LVL;
}

/** Flooding-rate REDUCTION as a fraction (0 at Lv1, −5%/level, capped −50%) — the Hull upgrade's effect. */
export function floodReductionOf(hullLvl: number): number {
  return Math.min(SHIP_UPGRADE.HULL_FLOOD_MAX, (hullLvl - 1) * SHIP_UPGRADE.HULL_FLOOD_PER_LVL);
}

/** Cargo hold capacity (doubloons) — matches server shipStats().cargo. */
export function cargoOf(cls: number, cargoLvl: number): number {
  return (RAID.CARGO_BASE + (cargoLvl - 1) * RAID.CARGO_PER_LVL) * SHIP_CLASS[resolveCls(cls)].cargoMul;
}

/** Guns per broadside (class only). */
export function cannonsOf(cls: number): number {
  return SHIP_CLASS[resolveCls(cls)].cannonsPerSide;
}

/**
 * Comparative 0..1 bars for the shipyard card — Speed / Firepower / Durability / Cargo / Turn — each
 * normalised across the whole fleet so the BEST hull in a dimension reads a full bar. Derived live from
 * SHIP_CLASS (so tuning the constants re-shapes the bars automatically). This is the visual that kills
 * "pricier = stronger": every hull is spiky, none is strictly best.
 *
 * • Speed / Turn / Cargo   → the class multiplier over the fleet max.
 * • Firepower              → guns ÷ reload (DPS proxy) over the fleet max — Frigate tops it (4 guns, fast
 *                            reload), Galleon close behind, Sloop lowest.
 * • Durability             → the class flood-resist bonus (the ONLY per-class defensive knob) mapped onto
 *                            a floored scale so the frailest hull still shows a small bar, not empty.
 */
export interface RoleBars { speed: number; firepower: number; durability: number; cargo: number; turn: number; }

const DUR_FLOOR = 0.3; // the frailest hull still shows this much bar (empty would read "cannot float")

export function shipRoleBars(cls: number): RoleBars {
  // fleet maxima (computed live so the bars follow any SHIP_CLASS retune)
  let speedMax = 0, fireMax = 0, cargoMax = 0, turnMax = 0, bonusMax = 0;
  for (let i = 0; i < SHIP_CLASS_COUNT; i++) {
    const d = SHIP_CLASS[i as ShipClassId];
    speedMax = Math.max(speedMax, d.speedMul);
    fireMax = Math.max(fireMax, d.cannonsPerSide / d.reloadMul);
    cargoMax = Math.max(cargoMax, d.cargoMul);
    turnMax = Math.max(turnMax, d.turnMul);
    bonusMax = Math.max(bonusMax, d.floodResistBonus);
  }
  const c = SHIP_CLASS[resolveCls(cls)];
  const dur = bonusMax > 0 ? DUR_FLOOR + (1 - DUR_FLOOR) * (c.floodResistBonus / bonusMax) : 1;
  return {
    speed: c.speedMul / speedMax,
    firepower: (c.cannonsPerSide / c.reloadMul) / fireMax,
    durability: dur,
    cargo: c.cargoMul / cargoMax,
    turn: c.turnMul / turnMax,
  };
}
